/*
  Speed patches for the Selleasy (upsell) and DealEasy apps (both by LogBase), and
  for the cart reads every app makes.

  How an add to cart with a Selleasy add-on ticked works without these patches:
  Selleasy catches the click, asks DealEasy to apply its offer code (DealEasy first
  sleeps 500 ms), adds the add-on in its own request, reads the cart, applies the
  same code again (another 500 ms sleep), then clicks the button again so the theme
  adds the product in a second request. The drawer fills ~3 s after the click.

  1. Merged add. A real click on an add-to-cart button opens a short transaction.
     When Selleasy sends its add-on request, the clicked form's item(s) and the
     drawer sections are added to it: one request adds both, and the drawer renders
     from its response. Selleasy's own re-click then reaches the form, whose submit
     is cancelled (the product is in the cart already). Selleasy still builds the
     add-on lines, discount codes and tracking itself.
  2. Discount helper (window.lbDealeasy.applyDiscountsToCart). DealEasy's 500 ms
     sleep before Selleasy's calls is skipped. In a transaction the first call runs
     at once (the code must be on the cart before the add, so the drawer shows
     the add-on discounted) and a repeat with the same codes is answered from it.
     Outside one, the first call of a burst waits for the follow-up Selleasy sends
     moments later, which applies the same codes (run anyway after 2 s).
  3. Cart reads. Selleasy, DealEasy and Alia each read /cart.js after every change,
     at the same moment. Reads that overlap share one request; any cart change in
     between starts a new one.

  Update safety: each patch checks what it relies on when it runs (the helper's
  source for the sleep, the shape of Selleasy's request, a cart drawer on the
  page) and leaves the app's own behaviour in place when something differs. App
  versions are compared with the ones this was tested with; a different version
  is reported in the console and in osAppPatches.versions, and the checks decide
  per call. osAppPatches.applied lists what is active; localStorage
  'os-app-patches-off' = '1' turns everything off for this browser.
*/
(() => {
  if (window.osAppPatches) return;

  let disabled = false;
  try {
    disabled = localStorage.getItem('os-app-patches-off') === '1';
  } catch (error) {}

  const api = (window.osAppPatches = {
    applied: [],
    versions: {},
    tested: { 'lb-upsell': 463, dealeasy: 297 }, // re-checked 2026-10-22
    disabled,
  });
  if (disabled) return;

  const note = (name) => api.applied.includes(name) || api.applied.push(name);
  const warn = (...args) => console.warn('[os-app-patches]', ...args);

  /* ---------- App versions ---------- */

  let versionsReported = false;
  function checkVersions() {
    document.querySelectorAll('script[src*="/extensions/"]').forEach((script) => {
      const match = script.src.match(/\/(lb-upsell|dealeasy)-(\d+)\//);
      if (match) api.versions[match[1]] = Number(match[2]);
    });
    const changed = Object.entries(api.tested).filter(
      ([app, version]) => api.versions[app] && api.versions[app] !== version
    );
    if (changed.length && !versionsReported) {
      versionsReported = true;
      console.info(
        '[os-app-patches] app updated since the patches were tested:',
        changed.map(([app, version]) => `${app} ${version} → ${api.versions[app]}`).join(', '),
        '— patches check themselves per call; re-test add to cart with an add-on.'
      );
    }
  }

  /* ---------- Add-to-cart transaction ---------- */

  const TXN_MS = 10000;
  let txn = null; // { form, button, started, merged, response, discount }

  const activeTxn = () => (txn && Date.now() - txn.started < TXN_MS ? txn : (txn = null));

  function addButton(target) {
    const button = target?.closest?.('[name="add"]');
    if (!button || !button.closest('product-form') || !button.form) return null;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return null;
    return button;
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = addButton(event.target);
      if (!button) return;
      if (event.isTrusted) {
        checkVersions();
        // Only where the drawer can be filled from a merged response.
        txn =
          document.querySelector('cart-drawer') && !button.closest('quick-add-modal')
            ? { form: button.form, button, started: Date.now(), merged: false }
            : null;
      }
    },
    { capture: true }
  );

  // Selleasy's replay of the click submits the form: the product went in with the
  // add-on already, so the theme must not add it again.
  document.addEventListener(
    'submit',
    (event) => {
      const current = activeTxn();
      if (!current?.merged || event.target !== current.form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      txn = null;
    },
    { capture: true }
  );

  // For instant-cart-drawer.js: the replayed click must not reopen the placeholder.
  api.isReplayClick = (event) => {
    const current = activeTxn();
    return !event.isTrusted && !!current?.merged && addButton(event.target)?.form === current.form;
  };

  // The clicked form's lines, as /cart/add.js JSON items.
  function formItems(form) {
    const data = new FormData(form);
    const id = Number(data.get('id'));
    if (!id) return null;
    const item = { id, quantity: Number(data.get('quantity')) || 1 };
    const plan = data.get('selling_plan');
    if (plan) item.selling_plan = Number(plan);
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    return [item];
  }

  /* ---------- fetch: merged add and shared cart reads ---------- */

  const CART_READ = /\/cart\.js(on)?$/;
  const CACHE_BUSTERS = ['ts', '_', 't', 'timestamp', 'v'];
  const REUSE_MS = 300;
  let cartVersion = 0;
  let sharedRead = null; // { version, promise, done }

  function parseUrl(input) {
    try {
      const href = typeof input === 'string' ? input : input instanceof URL ? input.href : null;
      return href ? new URL(href, location.origin) : null;
    } catch (error) {
      return null;
    }
  }

  const isCartMutation = (url, method) =>
    url && method !== 'GET' && url.origin === location.origin && /\/cart\/(add|change|update|clear)/.test(url.pathname);

  // Storefront API mutations (DealEasy and Selleasy set discount codes this way)
  // change the cart too, on this domain or the myshopify one.
  const isStorefrontMutation = (url, method, init) =>
    url &&
    method === 'POST' &&
    /\/api\/[^/]+\/graphql\.json$/.test(url.pathname) &&
    typeof init?.body === 'string' &&
    /\bmutation\b/.test(init.body);

  const isCartRead = (url, init) =>
    url &&
    url.origin === location.origin &&
    CART_READ.test(url.pathname) &&
    (!init || !init.method || init.method.toUpperCase() === 'GET') &&
    !init?.body &&
    [...url.searchParams.keys()].every((key) => CACHE_BUSTERS.includes(key));

  function isSelleasyAdd(url, init) {
    if (!url || url.searchParams.get('src') !== 'selleasy' || !/\/cart\/add/.test(url.pathname)) return null;
    if (typeof init?.body !== 'string') return null;
    try {
      const body = JSON.parse(init.body);
      // The shape Selleasy sent when this was written: add-on lines tagged _lb-product.
      const ok =
        Array.isArray(body.items) &&
        body.items.length &&
        body.items.every((item) => item && item.id && item.properties && '_lb-product' in item.properties);
      return ok ? body : null;
    } catch (error) {
      return null;
    }
  }

  function jsonResponse(json, status = 200) {
    return new Response(JSON.stringify(json), { status, headers: { 'Content-Type': 'application/json' } });
  }

  const nativeFetch = window.fetch;

  async function mergedAdd(current, url, init, body) {
    const drawer = document.querySelector('cart-drawer');
    const items = formItems(current.form);
    if (!drawer || !items) return nativeFetch(url.href, init);

    const sections = drawer.getSectionsToRender().map((section) => section.id);
    const merged = { ...body, items: [...body.items, ...items], sections, sections_url: location.pathname };

    const response = await nativeFetch(url.href, { ...init, body: JSON.stringify(merged) });
    if (!response.ok) {
      // Most likely the product itself (sold out, limit): add the add-on alone, as
      // Selleasy would, and let the replayed click add the product with the theme's
      // usual error message.
      return nativeFetch(url.href, init);
    }

    const json = await response.json();
    current.merged = true;
    current.response = json;
    note('merged-add');
    try {
      drawer.renderContents(json);
      // As product-form.js does after an add: an empty cart's drawer is marked empty.
      drawer.classList.remove('is-empty');
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
      }
    } catch (error) {
      warn('drawer render failed', error);
    }
    return jsonResponse(json, response.status);
  }

  // Reads share a request only within one cart version: every change bumps the
  // version when it starts and when it ends, so a read never gets an answer that
  // began before a change it should see. (Reads made while a change is still
  // finishing, as apps do from their response handlers, share among themselves.)
  function sharedCartRead(input, init) {
    const now = Date.now();
    if (
      sharedRead &&
      sharedRead.version === cartVersion &&
      (!sharedRead.done || now - sharedRead.done < REUSE_MS)
    ) {
      note('shared-cart-reads');
      return sharedRead.promise.then((response) => response.clone());
    }
    const read = { version: cartVersion, done: 0 };
    read.promise = nativeFetch(input, init).then(
      (response) => {
        read.done = Date.now();
        if (!response.ok) sharedRead = null;
        return response;
      },
      (error) => {
        sharedRead = null;
        throw error;
      }
    );
    sharedRead = read;
    return read.promise.then((response) => response.clone());
  }

  window.fetch = function (input, init) {
    try {
      const url = parseUrl(input);
      const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();

      if (isCartRead(url, init)) return sharedCartRead(input, init);

      if (isCartMutation(url, method)) {
        cartVersion += 1;
        const settle = () => (cartVersion += 1);

        const current = activeTxn();
        const body = current && !current.merged ? isSelleasyAdd(url, init) : null;
        const request = body ? mergedAdd(current, url, init, body) : nativeFetch.apply(this, arguments);
        request.then(settle, settle);
        return request;
      }

      if (isStorefrontMutation(url, method, init)) {
        cartVersion += 1;
        const request = nativeFetch.apply(this, arguments);
        const settle = () => (cartVersion += 1);
        request.then(settle, settle);
        return request;
      }
    } catch (error) {
      warn('fetch patch skipped', error);
    }
    return nativeFetch.apply(this, arguments);
  };
  note('fetch');

  // Cart changes made with XMLHttpRequest also end a shared read.
  try {
    const open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (isCartMutation(parseUrl(String(url)), String(method).toUpperCase())) {
          cartVersion += 1;
          this.addEventListener('loadend', () => (cartVersion += 1));
        }
      } catch (error) {}
      return open.apply(this, arguments);
    };
  } catch (error) {
    warn('XMLHttpRequest patch skipped', error);
  }

  /* ---------- DealEasy discount helper ---------- */

  const BURST_MS = 3000; // calls closer than this belong to one add to cart
  const FALLBACK_MS = 2000; // run a held call if Selleasy does not follow up

  const sameCodes = (a, b) =>
    Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((code) => b.includes(code));

  function patchHelper(dealeasy) {
    const original = dealeasy?.applyDiscountsToCart;
    if (typeof original !== 'function' || original.osPatched) return;

    checkVersions();
    const source = Function.prototype.toString.call(original);
    // The helper still sleeps 500 ms for Selleasy, as it did when this was written.
    const skipSleep = /"selleasy"===\w+&&await[\w.]+\(500\)/.test(source.replace(/\s/g, ''));

    let lastCall = 0;
    let held = null;

    const run = (self, args) => {
      if (!skipSleep) return original.apply(self, args);
      const nativeSetTimeout = window.setTimeout;
      window.setTimeout = function (handler, delay, ...rest) {
        return nativeSetTimeout.call(window, handler, delay === 500 ? 0 : delay, ...rest);
      };
      try {
        return original.apply(self, args);
      } finally {
        window.setTimeout = nativeSetTimeout;
      }
    };

    const patched = function (codes, src, ...rest) {
      try {
        if (src !== 'selleasy') return original.call(this, codes, src, ...rest);
        const args = [codes, src, ...rest];
        const now = Date.now();
        const firstOfBurst = now - lastCall > BURST_MS;
        lastCall = now;

        const current = activeTxn();
        if (current) {
          if (held) {
            clearTimeout(held.timer);
            held = null;
          }
          // A repeat with the same codes is answered from the first call, unless that
          // one failed (DealEasy resolves false, e.g. for a cart Shopify has not
          // created yet): then the repeat applies them.
          if (current.discount && sameCodes(current.discount.codes, codes)) {
            return current.discount.promise.then((applied) => applied || run(this, args));
          }
          const promise = run(this, args);
          current.discount = { codes: [...(codes || [])], promise };
          return promise;
        }

        if (held) {
          // The follow-up: it carries the same offer codes for the current cart.
          clearTimeout(held.timer);
          held = null;
        } else if (firstOfBurst) {
          held = {
            timer: setTimeout(() => {
              held = null;
              run(this, args);
            }, FALLBACK_MS),
          };
          return Promise.resolve(true);
        }
        return run(this, args);
      } catch (error) {
        warn('falling back to DealEasy', error);
        return original.call(this, codes, src, ...rest);
      }
    };

    patched.osPatched = true;
    dealeasy.applyDiscountsToCart = patched;
    note(skipSleep ? 'dealeasy-discounts' : 'dealeasy-discounts (sleep kept)');
  }

  // DealEasy sets window.lbDealeasy once its setup loads; patch it then, and again
  // if it is ever replaced.
  let current = window.lbDealeasy;
  patchHelper(current);
  try {
    Object.defineProperty(window, 'lbDealeasy', {
      configurable: true,
      enumerable: true,
      get: () => current,
      set: (value) => {
        current = value;
        try {
          patchHelper(value);
        } catch (error) {
          warn(error);
        }
      },
    });
  } catch (error) {
    patchHelper(window.lbDealeasy);
  }
})();
